import { useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/el-houda_logo.svg";
import useForm from "../hooks/useForm";
import AuthService from "../services/authService";
import UserContext from "../context/UserContext";
import { initializeTheme } from "../utils/theme";
import "./login.css"; // Import CSS statically for reliable loading

export function Login() {
    const navigate = useNavigate();
    const { login } = useContext(UserContext);
    const {
        values: formInput,
        errors,
        isLoading,
        setIsLoading,
        handleChange,
        validateForm,
        resetForm
    } = useForm({ username: "", password: "" });

    useEffect(() => {
        if (AuthService.isAuthenticated()) {
            navigate('/home');
            return;
        }

        // Apply theme immediately to prevent flash
        initializeTheme();

        // Add login route class to body for background styling, remove dashboard class
        document.body.classList.add('login-route');
        document.body.classList.remove('dashboard-route');

        return () => {
            document.body.classList.remove('login-route');
        };
    }, [navigate]);
    return (
        <div className="login-page bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center min-h-screen">
            <div className="login-card bg-white bg-opacity-90 p-8 rounded-lg shadow-lg w-full max-w-md">
                <div className="login-header text-center">
                    <div className="logo-wrap mb-4">
                        <img src={logo} alt="El-Houda logo" className="logo-img mx-auto" />
                    </div>
                    <h1 className="text-2xl font-bold text-green-700 mb-2">El-Houda Group Panel</h1>
                    <p className="text-green-600">Sign in to access your dashboard</p>
                </div>

                <form
                    className="login-form space-y-4"
                    onSubmit={async (e) => {
                        e.preventDefault();
                        if (!validateForm()) return;
                        
                        setIsLoading(true);
                        try {
                            const data = await AuthService.login(formInput);
                            console.log('Login response data:', data);
                            
                            if (!data || !data.token) {
                                throw new Error('Invalid response from server: No token received');
                            }
                            
                            AuthService.setToken(data.token);

                            // Set user data in context (assuming data contains user info)
                            login({
                                username: data.username || formInput.username,
                                email: data.email || '',
                                permissions: data.permissions || []
                            });

                            // Verify token was set correctly
                            const storedToken = AuthService.getToken();
                            if (!storedToken) {
                                throw new Error('Failed to save authentication token');
                            }

                            resetForm();
                            navigate("/home");
                        } catch (error) {
                            console.error('Operation error:', error);
                            alert(error.message || 'Operation failed. Please try again.');
                        } finally {
                            setIsLoading(false);
                        }
                    }}>

                    <label htmlFor="username">Username</label>
                    <input
                        id="username"
                        className={`input-field ${errors.username ? 'error' : ''} focus:ring-2 focus:ring-green-500`}
                        value={formInput.username}
                        name="username"
                        type="text"
                        placeholder="Username"
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                    {errors.username && <span className="error-message text-red-500">{errors.username}</span>}

                    <label htmlFor="password">Password</label>
                    <input
                        id="password"
                        className={`input-field ${errors.password ? 'error' : ''} focus:ring-2 focus:ring-green-500`}
                        value={formInput.password}
                        name="password"
                        type="password"
                        placeholder="Password"
                        onChange={handleChange}
                        disabled={isLoading}
                    />
                    {errors.password && <span className="error-message text-red-500">{errors.password}</span>}

                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={isLoading}
                    >
                        {isLoading ? <span className="loader"></span> : "Login"}
                    </button>
                </form>

                <p className="footer-note text-center text-white-900 mt-4">© 2025 El-Houda Group. All rights reserved.</p>
            </div>
        </div>
    )
}

export default Login;
